import useHousePrices from './useHousePrices';
import { useQuery } from 'react-query';
import {
  calculatePropertyTrends,
  calculateSameTypeStats,
  determinePropertyType,
  formatAllSalesData,
  getCurrentFlatNumber,
  getCurrentPropertyDetails,
  prepareRegionalPriceData,
} from './helpers/chartDataHelpers';
import { useDerivedSales } from './useDerivedSales';

export const usePreparePropertyChartData = ({ validatedAddress, postcode }) => {
  const { data: housePricesData, isLoading: isLoadingHousePriceData } = useHousePrices(postcode);
  const sales = housePricesData?.sales ?? [];
  const { data: derivedData, isLoading: isLoadingDerivedSales } = useDerivedSales({
    validatedAddress,
  });

  const safeDerivedData =
    isLoadingDerivedSales || !derivedData
      ? { currentPropertySales: null, areaSales: null, propertyAddress: null }
      : derivedData;

  const { currentPropertySales } = safeDerivedData;

  console.log(
    'testing',
    'sales',
    sales,
    'currentPropertySales',
    currentPropertySales,
    'isLoadingDerivedSales',
    isLoadingDerivedSales,
    'isLoadingHousePriceData',
    isLoadingHousePriceData,
    'housePricesData',
    housePricesData,
  );

  console.log(
    'enabled',
    !isLoadingDerivedSales && !isLoadingHousePriceData,
    sales,
    currentPropertySales,
    !isLoadingDerivedSales &&
      !isLoadingHousePriceData &&
      validatedAddress &&
      Boolean(
        sales && sales.length > 0 && currentPropertySales && currentPropertySales?.length > 0,
      ),
  );
  return useQuery(
    // Use stable key parts: number of sales, and a string key from validatedAddress.
    [
      'preparePropertyChartData',
      validatedAddress?.street_number,
      validatedAddress?.street,
      postcode,
    ],
    () => {
      console.log('calculating prepared property chart data');
      const { currentPaon, currentStreet } = getCurrentPropertyDetails({
        validatedAddress,
        currentPropertySales,
        sales,
      });

      const currentPropertyType = determinePropertyType({
        validatedAddress,
        sales,
        currentPaon,
        currentStreet,
      });

      // Compute flat number.
      const currentFlatNumber = getCurrentFlatNumber(validatedAddress);

      // Format all sales data.
      const allSalesData = formatAllSalesData(sales, {
        currentPaon,
        currentStreet,
        currentFlatNumber,
      });

      const currentPropertyData = allSalesData.filter((item) => item.isCurrentProperty);
      const sameTypeData = allSalesData.filter((item) => {
        if (item.isCurrentProperty) return false;
        const itemType = (item.propertyType || '').toLowerCase();
        const currentType = currentPropertyType.toLowerCase();
        return (
          itemType === currentType ||
          itemType.includes(currentType) ||
          currentType.includes(itemType)
        );
      });

      const averagePrice =
        currentPropertyData.length > 0
          ? Math.round(
              currentPropertyData.reduce((sum, item) => sum + item.y, 0) /
                currentPropertyData.length,
            )
          : 0;

      // Determine property address.
      let propertyAddress = '';
      if (validatedAddress) {
        if (validatedAddress.flat_number) {
          propertyAddress =
            `Flat ${validatedAddress.flat_number}, ${validatedAddress.street_number} ${validatedAddress.street}`.trim();
        } else if (validatedAddress.street_number && validatedAddress.street) {
          propertyAddress = `${validatedAddress.street_number} ${validatedAddress.street}`.trim();
        }
      } else if (currentPropertyData.length > 0) {
        propertyAddress = currentPropertyData[0].address;
      } else {
        propertyAddress = 'Current Property';
      }

      // Calculate property trends.
      const propertyTrends = calculatePropertyTrends(currentPropertyData);

      // Prepare regional price index data.
      const regionPriceData = prepareRegionalPriceData(housePricesData, currentPropertyType);

      // Calculate statistics for same–type properties.
      const sameTypeStats = sameTypeData.length > 0 ? calculateSameTypeStats(sameTypeData) : null;

      console.log('Same property type statistics:', sameTypeStats);

      // Build the series data for charting.
      const series = [
        {
          name: propertyAddress,
          data: currentPropertyData,
          borderColor: '#3182CE',
          backgroundColor: 'rgba(49, 130, 206, 0.2)',
          borderWidth: 3,
          pointBackgroundColor: '#3182CE',
          pointRadius: 5,
          pointHoverRadius: 7,
        },
        {
          name: `Similar ${
            currentPropertyType.charAt(0).toUpperCase() + currentPropertyType.slice(1)
          } Properties`,
          data: sameTypeData,
          borderColor: '#68D391',
          backgroundColor: 'rgba(104, 211, 145, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#68D391',
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        // Add the regional price index series if available.
        ...(regionPriceData && regionPriceData.length > 0
          ? [
              {
                name: `${housePricesData.regionPriceIndex.region} ${
                  currentPropertyType.charAt(0).toUpperCase() + currentPropertyType.slice(1)
                } Average`,
                data: regionPriceData,
                borderColor: '#F6AD55',
                backgroundColor: 'rgba(246, 173, 85, 0.1)',
                borderWidth: 2,
                borderDash: [5, 5],
                pointBackgroundColor: '#F6AD55',
                pointRadius: 2,
                pointHoverRadius: 4,
              },
            ]
          : []),
      ];

      return {
        series,
        propertyAddress,
        postcode,
        averagePrice,
        isCurrentProperty: true,
        propertyTrends,
        sameTypeStats,
        currentPropertyType,
      };
    },
    {
      enabled:
        !isLoadingDerivedSales &&
        !isLoadingHousePriceData &&
        validatedAddress &&
        Boolean(
          sales && sales.length > 0 && currentPropertySales && currentPropertySales?.length > 0,
        ),
      staleTime: Infinity,
    },
  );
};
